import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, downloadFile, errMsg } from "@/lib/api";

const TONE = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", merged: "bg-sky-50 text-sky-700 border-sky-200", duplicate: "bg-red-50 text-red-700 border-red-200", similar: "bg-amber-50 text-amber-700 border-amber-200", error: "bg-slate-100 text-slate-700 border-slate-300", unchanged: "bg-slate-50 text-slate-500 border-slate-200" };

export function ImportDialog({ entity, open, onOpenChange, onDone, path, title, commitLabel = "Import", templatePath, templateName, templateLabel = "Download template", hint }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (commit) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose an Excel or CSV file");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`${path || `/${entity}/import`}?commit=${commit}`, fd);
      setPreview(r.data);
      if (commit) {
        toast.success(`${r.data.created} ${entity === "access" ? "change" : "record"}${r.data.created === 1 ? "" : "s"} ${path && entity !== "legacy_lms" ? "applied" : "imported"}`);
        onDone?.();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    if (!v) setPreview(null);
    onOpenChange(v);
  };

  const key = entity === "parts" ? "part_code" : "name";
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${entity}-import-dialog`}>
        <DialogHeader><DialogTitle>{title || `Import ${entity} from Excel`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => downloadFile(templatePath || `/templates/${entity}`, templateName || `${entity}_template.xlsx`)} data-testid="import-template-btn">
              <Download className="h-4 w-4 mr-1" /> {templateLabel}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={() => setPreview(null)} className="text-sm" data-testid="import-file-input" />
          </div>
          <p className="text-xs text-slate-500">
            Step 1: check the file. {hint || (entity === "legacy_lms" ? "Each row becomes an approved historical LM with its original number; LM numbers already in the register are skipped and the customer must exist in the Customers Master." : path ? "Each customer is matched by GSTIN or name; only the fields that differ are updated." : entity === "lookups" ? "Values already in the same list (by name or alias) and errors are skipped." : `Duplicates (same ${entity === "parts" ? "part code" : "GSTIN or name"}) and errors are skipped.`)}
            {entity === "customers" && !path && " Similar names are skipped too — add them manually after review."}
          </p>
          {preview && (
            <div data-testid="import-preview">
              <div className="flex flex-wrap gap-3 text-sm mb-2">
                {Object.entries(preview.summary).map(([k, v]) => (
                  <span key={k} data-testid={`import-summary-${k}`} className={`px-2 py-0.5 rounded border text-xs font-semibold uppercase tracking-wider ${TONE[k]}`}>{k}: {v}</span>
                ))}
              </div>
              <div className="max-h-72 overflow-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead><tr className="th text-left"><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Record</th><th className="px-2 py-1.5">Result</th><th className="px-2 py-1.5">Detail</th></tr></thead>
                  <tbody>
                    {preview.results.map((r) => (
                      <tr key={r.row} className="border-b border-slate-100">
                        <td className="px-2 py-1.5 font-mono">{r.row}</td>
                        <td className="px-2 py-1.5">{r[key]}</td>
                        <td className="px-2 py-1.5 uppercase font-mono">{r.status}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          {!preview?.committed && <Button variant="outline" onClick={() => run(false)} disabled={busy} data-testid="import-preview-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Check file</Button>}
          {preview && !preview.committed && (
            <Button onClick={() => run(true)} disabled={busy || !preview.summary.ok} data-testid="import-commit-btn">
              {commitLabel} {preview.summary.ok} valid rows
            </Button>
          )}
          {preview?.committed && <Button onClick={() => close(false)} data-testid="import-close-btn">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
